<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <AppHeader />

    <div class="max-w-7xl mx-auto px-4 py-8 w-full">
      <h1 class="text-3xl font-semibold mb-2">Publicar minha Demanda</h1>
      <p class="text-gray-500 text-sm mb-6">Descreva o que você precisa e receba propostas de freelancers qualificados.</p>

      <!-- Banner de alerta para freelancers -->
      <div class="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-2xl px-5 py-4 mb-6">
        <svg class="w-5 h-5 text-amber-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
        <div>
          <p class="text-amber-800 text-sm font-semibold">Esta área é exclusiva para quem quer <span class="underline">contratar</span> um profissional remoto para executar uma tarefa.</p>
          <p class="text-amber-700 text-sm mt-1">
            <strong>Não é o lugar certo</strong> para divulgar seu currículo, portfólio, serviços ou produto.
          </p>
          <p class="text-amber-700 text-sm mt-0.5">
            Se você é freelancer remoto ou prestador e quer <strong>oferecer seus serviços</strong>, não publique aqui —
            <router-link to="/projetos" class="font-semibold underline hover:text-amber-900">veja os projetos abertos</router-link>
            e envie sua proposta diretamente para quem está contratando.
          </p>
        </div>
      </div>

      <div class="bg-white shadow rounded-2xl p-6 mb-8">
        <div class="grid gap-6">

          <div>
            <label class="text-sm font-medium text-gray-700 mb-1 block">
              Título da Demanda <span class="text-red-500">*</span>
            </label>

            <input
              v-model="form.title"
              type="text"
              maxlength="250"
              placeholder="Ex: Criação de Landing Page para Captar Leads, Desenvolvimento de Aplicativo Delivery, Designer para Criar Logotipo"
              class="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52] transition"
            />

            <p class="text-xs text-gray-400 mt-1">
              Informe apenas um <strong>título curto e objetivo</strong> para sua demanda.
              Os detalhes completos devem ser preenchidos no campo abaixo.
            </p>
          </div>

          <div>
            <label class="text-sm font-medium text-gray-700 mb-1 block">Descreva em detalhes o que você precisa contratar <span class="text-red-500">*</span></label>
            <textarea v-model="form.description" rows="6" placeholder="Explique a tarefa que você precisa que alguém execute: o resultado esperado, prazo, referências, formatos, público-alvo, etc. Quanto mais detalhe, melhores serão as propostas que você vai receber."
              class="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52] transition"></textarea>
          </div>

          <!-- CATEGORIAS (busca + dropdown + chips) -->
          <div>
            <label class="text-sm font-medium">Categorias</label>

            <!-- busca -->
            <div class="relative mt-2">
              <input
                :value="categorySearch"
                @input="onCategorySearch"
                type="text"
                placeholder="Digite para buscar categorias..."
                class="w-full border border-gray-200 rounded-xl px-4 py-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52] transition"
              />
              <span
                v-if="categoryLoading"
                class="mdi mdi-loading animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-[#b99a52] text-lg pointer-events-none"
              ></span>

              <!-- dropdown -->
              <div
                ref="categoryDropdownRef"
                v-if="categoryLoading || categoryOptions.length"
                class="absolute top-full mt-1 left-0 right-0 bg-white border rounded-xl shadow-lg z-10 max-h-56 overflow-auto"
                :style="categoryDropdownStyle"
              >
                <div v-if="categoryLoading" class="px-4 py-3 flex items-center gap-2 text-sm text-gray-400">
                  <span class="mdi mdi-loading animate-spin text-base shrink-0"></span>
                  <span>Buscando categorias...</span>
                </div>
                <template v-else>
                  <button
                    v-for="opt in categoryOptions"
                    :key="opt.id"
                    type="button"
                    @click="addCategory(opt)"
                    class="w-full text-left px-3 py-2.5 hover:bg-gray-50 active:bg-gray-100 text-sm border-b border-gray-100 last:border-0"
                  >
                    {{ opt.description }}
                  </button>
                </template>
              </div>
            </div>
          </div>

          <!-- chips -->
          <div v-if="form.categories.length" class="flex flex-wrap gap-2 mt-2">
              <button v-for="cat in form.categories" :key="cat.id" type="button"
                class="bg-[#1a1a1a] text-white px-3 py-1 rounded-full flex items-center gap-2"
                @click="removeCategoryById(cat.id)" title="Remover">
                <span class="text-sm">{{ cat.description }}</span>
                <span class="text-red-400 text-sm">×</span>
              </button>
            </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="flex flex-col">
              <label class="mb-1 font-medium">Urgência</label>
              <select v-model="form.urgency_level" class="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52] transition">
                <option value="low">Baixa</option>
                <option value="medium">Média</option>
                <option value="high">Alta</option>
              </select>
            </div>

            <!--<div class="flex flex-col">
              <label class="mb-1 font-medium">Orçamento (R$)</label>
              <input v-model="form.budget" type="number" step="0.01" class="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52] transition" />
            </div>-->

            <div class="flex flex-col">
              <label class="mb-1 font-medium">Prazo final</label>
              <input v-model="form.deadline" type="date" class="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52] transition" />
            </div>
          </div>

          <div>
            <label class="font-medium mb-2 block">Anexos (PDF, DOC, DOCX, PNG, JPG, JPEG, WEBP)</label>
            <input @change="handleFiles" type="file" multiple accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
              class="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52] transition" />

            <div v-if="files.length" class="mt-4 space-y-2">
              <div v-for="(file, index) in files" :key="file.name + index"
                class="flex justify-between items-center bg-gray-100 p-3 rounded-xl">
                <div class="flex items-center gap-3">
                  <span class="text-sm">{{ file.name }}</span>
                  <span class="text-xs text-gray-500">({{ formatBytes(file.size) }})</span>
                </div>
                <button @click="removeFile(index)" class="text-red-600 text-sm">Remover</button>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div class="flex justify-end gap-4 mt-6">
        <button @click="resetForm" class="px-6 py-3 rounded-xl border border-gray-400">Limpar</button>
        <button @click="confirmSubmission" class="px-6 py-3 rounded-xl bg-[#1a1a1a] text-white">Publicar Demanda</button>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, nextTick } from 'vue'
import { logContractorInterest } from '@/services/contractorInterestLog'
import api from '@/services/api';
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import { useRouter, useRoute } from "vue-router";

const { proxy } = getCurrentInstance()

const router = useRouter();
const form = ref({
  title: '',
  description: '',
  category_id: '',
  urgency_level: 'low',
  budget: '',
  deadline: '',
  categories: []
})

const categorySearch = ref('')
const categoryOptions = ref([])
const categoryLoading = ref(false)
const categoryDropdownRef = ref(null)
const categoryDropdownStyle = ref({})
let catTimer = null

function clipDropdownToViewport (dropdownEl, styleRef) {
  if (!dropdownEl) return
  nextTick(() => {
    const vvHeight = window.visualViewport?.height ?? window.innerHeight
    const rect = dropdownEl.getBoundingClientRect()
    const available = vvHeight - rect.top - 8
    if (available > 0 && available < rect.height) {
      styleRef.value = { maxHeight: Math.max(available, 80) + 'px' }
    } else {
      styleRef.value = {}
    }
  })
}

function onCategorySearch (event) {
  if (event?.target) categorySearch.value = event.target.value
  clearTimeout(catTimer)

  const term = categorySearch.value.trim()
  if (term.length < 2) {
    categoryOptions.value = []
    categoryLoading.value = false
    return
  }

  categoryLoading.value = true

  catTimer = setTimeout(async () => {
    try {
      const { data } = await api.get('/categories', { params: { search: term, type: 'remote', limit: 20 } })
      const list = data.data || []
      const selectedIds = new Set((form.value.categories || []).map(c => c.id))
      categoryOptions.value = list.filter(c => !selectedIds.has(c.id))
      if (categoryOptions.value.length) clipDropdownToViewport(categoryDropdownRef.value, categoryDropdownStyle)
    } catch {
      categoryOptions.value = []
    } finally {
      categoryLoading.value = false
    }
  }, 250)
}

const files = ref([])

onMounted(() => {
  // Evita duplicidade quando o usuário veio do botão "Criar Projeto" no header
  try {
    const skip = sessionStorage.getItem('contractorInterest:skipNextRemoteMount')
    if (skip === '1') {
      sessionStorage.removeItem('contractorInterest:skipNextRemoteMount')
      return
    }
  } catch (_) {}

  // Só registra lead se estiver logado
  try {
    const raw = localStorage.getItem('userData')
    const token = raw ? JSON.parse(raw)?.token : null
    if (token) logContractorInterest({ type: 'remote' })
  } catch (_) {}
})



function addCategory(opt) {
  if (!opt?.id) return

  const exists = form.value.categories.some(c => c.id === opt.id)
  if (!exists) {
    form.value.categories.push({ id: opt.id, description: opt.description })
  }

  categorySearch.value = ''
  categoryOptions.value = []
  categoryDropdownStyle.value = {}
}

function removeCategoryById(id) {
  form.value.categories = form.value.categories.filter(c => c.id !== id)
}

const handleFiles = (event) => {
  const incoming = Array.from(event.target.files || [])
  const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg', 'image/webp']
  const filtered = incoming.filter(f => allowed.includes(f.type) || /\.pdf|\.docx?|\.png|\.jpe?g|\.webp$/i.test(f.name))
  files.value = files.value.concat(filtered)
}

const removeFile = (index) => {
  files.value.splice(index, 1)
}

const resetForm = () => {
  form.value = {
    title: '',
    description: '',
    category_id: '',
    urgency_level: 'low',
    budget: '',
    deadline: '',
    categories: []
  }
  files.value = []
  categorySearch.value = ''
  categoryOptions.value = []
}

const sendProject = async () => {
  try {
    if (!form.value.title.trim()) {
      proxy.$swal.fire({ icon: 'warning', title: 'Título obrigatório' })
      return
    }
    if (!form.value.description.trim()) {
      proxy.$swal.fire({ icon: 'warning', title: 'Descrição obrigatória' })
      return
    }
    if (!form.value.deadline.trim()) {
      proxy.$swal.fire({ icon: 'warning', title: 'Prazo final obrigatório' })
      return
    }

    const payload = new FormData()
    payload.append('title', form.value.title)
    payload.append('description', form.value.description)
    payload.append('category_id', form.value.category_id || '')
    payload.append('urgency_level', form.value.urgency_level)
    payload.append('budget', form.value.budget || '')
    payload.append('deadline', form.value.deadline || '')

    /*const categoriesString = form.value.categories
      .map(c => c.description)
      .join(', ')
    payload.append('categories', categoriesString)*/
    payload.append("categories", JSON.stringify(form.value.categories));

    files.value.forEach((file) => {
      payload.append('attachments[]', file)
    })

    await api.post('/projects', payload, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    proxy.$swal.fire({ icon: 'success', title: 'Demanda publicada com sucesso! Em breve você receberá propostas de freelancers.' })
    resetForm()
    router.push('/projetos');

  } catch (error) {
    console.log(error)
    proxy.$swal.fire({
      icon: 'error',
      title: '',
      text: error?.response?.data?.error || 'Tente novamente mais tarde.'
    })
  }
}

const confirmSubmission = async () => {
  // Validações
  if (!form.value.title.trim()) {
    proxy.$swal.fire({ icon: 'warning', title: 'Título obrigatório' })
    return
  }
  if (!form.value.description.trim()) {
    proxy.$swal.fire({ icon: 'warning', title: 'Descrição obrigatória' })
    return
  }

  // Exibe o modal com customização de cores
  const result = await proxy.$swal.fire({
    title: 'Confirmar Publicação da Demanda',
    text: "Você está publicando uma demanda para contratar um freelancer. Deseja continuar?",
    icon: 'question',
    showCancelButton: true,

    // Confirmação: Preto da aplicação, texto branco (padrão)
    confirmButtonColor: '#1a1a1a',

    // Cancelar: Define a cor do fundo do botão de cancelar como branco
    // e usa uma classe customizada para forçar o texto a ser escuro
    cancelButtonColor: '#ffffff',

            confirmButtonText: 'Sim, publicar minha demanda!',
    cancelButtonText: 'Cancelar',

    // Adiciona classe customizada ao botão de cancelar
    customClass: {
      confirmButton: 'swal2-confirm-btn-custom',
      cancelButton: 'swal2-cancel-btn-custom'
    },

  })

  // 3. Verifica o resultado
  if (result.isConfirmed) {
    await sendProject()
  }
}

// helper para mostrar tamanho dos arquivos
const formatBytes = (bytes, decimals = 1) => {
  if (!bytes) return '0 B'
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}
</script>

<style scoped>
/* ajuste mínimo, estético */
</style>
<style>
/* Estiliza o botão de Cancelar para ter texto escuro e borda */
.swal2-cancel-btn-custom {
  color: #1f2937 !important;
  /* Cor do texto (Dark Gray) */
  border: 1px solid #9ca3af !important;
  /* Borda sutil (Gray 400) */
  background-color: #ffffff !important;
  /* Fundo branco */
}

/* Garante que o hover também seja visível */
.swal2-cancel-btn-custom:hover {
  background-color: #f3f4f6 !important;
  /* Levemente cinza no hover */
}

.swal2-confirm-btn-custom {
  color: #ffffff !important;
  /* ← TEXTO BRANCO */
  background-color: #1a1a1a !important;
  border: none !important;
}

.swal2-confirm-btn-custom:hover {
  background-color: #000000 !important;
}
</style>